import { useState, useEffect, useCallback, useRef } from 'react';
import {
  CloudUpload as UploadIcon,
  Trash2 as DeleteIcon,
  RefreshCw as SyncIcon,
  FileText as FileIcon,
  CheckCircle as ReadyIcon,
  CircleAlert as ErrorIcon,
  Hourglass as PendingIcon,
} from 'lucide-react';
import { Button, Chip, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@coordio/ui';
import { InlineErrorPanel, LoadingSkeleton } from '@coordio/ui/layout';
import { agentService } from '../../services/agentService';
import { useNotificationStore } from '../../store/notificationStore';
import { useConfirmDialog } from '../common/ConfirmDialog';
import type { AgentKnowledgeFileDto } from '../../types/knowledgeFile';
import { KnowledgeFileStatus, KnowledgeFileStatusLabel } from '../../types/knowledgeFile';

interface AgentKnowledgeFileListProps {
  agentId: string;
  readOnly?: boolean;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const StatusChip = ({ status, syncError }: { status: KnowledgeFileStatus; syncError?: string | null }) => {
  const label = KnowledgeFileStatusLabel[status] ?? 'Unknown';
  const color: 'green' | 'red' | 'blue' | 'default' =
    status === KnowledgeFileStatus.Ready
      ? 'green'
      : status === KnowledgeFileStatus.Failed
        ? 'red'
        : status === KnowledgeFileStatus.Uploading || status === KnowledgeFileStatus.Processing
          ? 'blue'
          : 'default';
  const icon =
    status === KnowledgeFileStatus.Ready ? (
      <ReadyIcon size={14} />
    ) : status === KnowledgeFileStatus.Failed ? (
      <ErrorIcon size={14} />
    ) : (
      <PendingIcon size={14} />
    );

  const chip = <Chip label={label} color={color} icon={icon} variant="outlined" />;

  if (syncError) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{chip}</TooltipTrigger>
          <TooltipContent>{syncError}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  return chip;
};

export const AgentKnowledgeFileList = ({ agentId, readOnly = false }: AgentKnowledgeFileListProps) => {
  const confirm = useConfirmDialog();
  const [files, setFiles] = useState<AgentKnowledgeFileDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { addNotification } = useNotificationStore();

  const loadFiles = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await agentService.getKnowledgeFiles(agentId);
      setFiles(data);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load knowledge files';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleUpload = async (fileList: FileList | File[]) => {
    const filesToUpload = Array.from(fileList);
    if (filesToUpload.length === 0) return;

    setUploading(true);
    let successCount = 0;
    for (const file of filesToUpload) {
      try {
        await agentService.uploadKnowledgeFile(agentId, file);
        successCount++;
      } catch (err: any) {
        addNotification(
          err.response?.data?.message || `Failed to upload "${file.name}"`,
          'error'
        );
      }
    }
    if (successCount > 0) {
      addNotification(`${successCount} file(s) uploaded`, 'success');
    }
    setUploading(false);
    loadFiles();
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleUpload(e.target.files);
      e.target.value = '';
    }
  };

  const handleDelete = async (file: AgentKnowledgeFileDto) => {
    const ok = await confirm({
      title: 'Delete knowledge file',
      message: `Delete "${file.originalFileName}"? This will remove it from the provider and storage.`,
      confirmText: 'Delete',
    });
    if (!ok) return;
    try {
      await agentService.deleteKnowledgeFile(agentId, file.id);
      addNotification('File deleted', 'success');
      loadFiles();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to delete file', 'error');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await agentService.syncKnowledgeFiles(agentId);
      addNotification('Knowledge files synced to provider', 'success');
      loadFiles();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to sync files', 'error');
    } finally {
      setSyncing(false);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      if (readOnly || uploading) return;
      if (e.dataTransfer.files) {
        handleUpload(e.dataTransfer.files);
      }
    },
    [readOnly, uploading, agentId]
  );

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSkeleton className="h-7 w-7" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h6 className="text-lg font-semibold">Knowledge Files (RAG)</h6>
        <div className="flex gap-2">
          {!readOnly && (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSync}
                disabled={syncing || files.length === 0}
              >
                {syncing ? <LoadingSkeleton className="h-4 w-4 mr-1" /> : <SyncIcon size={16} className="mr-1" />}
                Sync
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? <LoadingSkeleton className="h-4 w-4 mr-1" /> : <UploadIcon size={16} className="mr-1" />}
                Upload
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={handleFileInput}
                accept=".pdf,.txt,.md,.docx,.doc,.csv,.json,.html,.xml,.pptx,.xlsx"
              />
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4">
          <InlineErrorPanel message={error} action={<Button type="button" variant="ghost" size="sm" onClick={() => setError(null)}>Dismiss</Button>} />
        </div>
      )}

      {uploading && <div className="mb-4 h-1 w-full bg-[var(--mui-palette-primary-main)] rounded animate-pulse" />}

      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`rounded transition-all duration-200 ${
          isDragOver
            ? 'border-2 border-dashed border-[var(--mui-palette-primary-main)] bg-[var(--mui-palette-action-hover)]'
            : 'border-2 border-dashed border-transparent'
        } ${files.length === 0 ? 'min-h-[160px] flex flex-col items-center justify-center' : 'flex flex-col items-stretch justify-start'}`}
      >
        {files.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-[var(--mui-palette-text-disabled)] mb-2 inline-flex"><FileIcon size={48} /></div>
            <p className="text-[var(--mui-palette-text-secondary)]">
              No knowledge files yet. Upload files to enable RAG-powered search during agent conversations.
            </p>
            <p className="text-xs text-[var(--mui-palette-text-disabled)] mt-1">
              Drag and drop files here, or use the Upload button. Supported: PDF, TXT, MD, DOCX, CSV, JSON, HTML, PPTX, XLSX
            </p>
          </div>
        ) : (
          <div className="border border-[var(--mui-palette-divider)] rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--mui-palette-divider)] bg-[var(--mui-palette-action-hover)]">
                  <th className="px-3 py-2 text-left">File Name</th>
                  <th className="px-3 py-2 text-left">Size</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Provider</th>
                  <th className="px-3 py-2 text-left">Uploaded</th>
                  {!readOnly && <th className="px-3 py-2 text-right w-20">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {files.map((file) => (
                  <tr key={file.id} className="border-b border-[var(--mui-palette-divider)] last:border-b-0">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <FileIcon size={16} />
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-sm truncate max-w-[300px] block">
                                {file.originalFileName}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>{file.originalFileName}</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-sm text-[var(--mui-palette-text-secondary)]">
                        {formatFileSize(file.fileSizeBytes)}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <StatusChip status={file.status} syncError={file.syncError} />
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-sm text-[var(--mui-palette-text-secondary)]">
                        {file.providerName ?? '-'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-sm text-[var(--mui-palette-text-secondary)]">
                        {file.createdAt ? new Date(file.createdAt).toLocaleDateString() : '-'}
                      </span>
                    </td>
                    {!readOnly && (
                      <td className="px-3 py-2 text-right">
                        <Button type="button" variant="ghost" size="icon" className="text-red-500" onClick={() => handleDelete(file)} aria-label="Delete">
                          <DeleteIcon size={16} />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
